import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PortalCashTransactionDetail, type CashTransactionDetail } from '@kioskgaming/ui';
import { usePermission } from '../../../hooks/useAuth';
import { apiClient } from '../../../services/api';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';

const CashTransactionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canView = usePermission('cashSettlementManagement');
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState<CashTransactionDetail | null>(null);

  useEffect(() => {
    if (!canView || !id) return;
    const createdAt = searchParams.get('created_at') ?? undefined;
    setLoading(true);
    void apiClient
      .getAdminCashTransactionDetail(id, createdAt)
      .then((res) => {
        const d = res.data;
        setTransaction({
          ...d.transaction,
          performedByType: String(
            d.transaction.performedByType ?? d.transaction.performed_by_type ?? 'admin',
          ),
          performedById: String(
            d.transaction.performedById ?? d.transaction.performed_by_id ?? '',
          ),
          buyerBalanceAfter: Number(
            d.transaction.buyerBalanceAfter ?? d.transaction.buyer_balance_after ?? 0,
          ),
          updatedAt: String(d.transaction.updatedAt ?? d.transaction.updated_at ?? ''),
          events: (d.events ?? []).map((ev) => ({
            id: String(ev.id),
            eventType: String(ev.eventType ?? ev.event_type ?? 'created'),
            actorType: String(ev.actorType ?? ev.actor_type ?? 'admin'),
            actorId: (ev.actorId ?? ev.actor_id) as string | null | undefined,
            payload: (ev.payload as Record<string, unknown>) ?? {},
            createdAt: String(ev.createdAt ?? ev.created_at ?? ''),
          })),
          creditTransaction: d.creditTransaction
            ? {
                id: String(d.creditTransaction.id),
                amount: Number(d.creditTransaction.amount ?? 0),
                balanceAfter: Number(
                  d.creditTransaction.balanceAfter ?? d.creditTransaction.balance_after ?? 0,
                ),
                description: (d.creditTransaction.description as string) ?? null,
                createdAt: String(d.creditTransaction.createdAt ?? d.creditTransaction.created_at ?? ''),
              }
            : null,
          performedBy: d.performedBy
            ? {
                id: String(d.performedBy.id),
                email: (d.performedBy.email as string) ?? null,
                name: (d.performedBy.name as string) ?? null,
              }
            : null,
        });
      })
      .catch((e: unknown) => {
        setTransaction(null);
        toast.error(e instanceof Error ? e.message : 'Could not load cash transaction');
      })
      .finally(() => setLoading(false));
  }, [canView, id, searchParams]);

  if (!canView) {
    return <AccessDeniedPanel message="You don't have permission to view cash transactions." />;
  }

  return (
    <PortalCashTransactionDetail
      loading={loading}
      transaction={transaction}
    />
  );
};

export default CashTransactionDetailPage;
